import React, { useEffect, useState } from "react";
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  
} from "@stripe/react-stripe-js";
import { useDispatch, useSelector } from "react-redux";

import { Box } from "../box";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Flex,
  Form,
  Input,
  Row,
  Select,
  Typography,
} from "antd";
import RequiredLabel from "../required-label/RequiredLabel";
import { useTranslation } from "react-i18next";
import { colors } from "../../../constants/colors";
import Spinner from "../spinner/Spinner";
import PaymentConfirmationDialog from "./PaymentConfirmationDialog";
import Actions from "../../../redux/actions";
import { toastService } from "../toast-messages/toast";
import "./StripePayment.scss";
import { sanitizeDataTypeClientSide } from "../../../utils/utils";
import { CheckCircleFilled, UserOutlined } from "@ant-design/icons";
import { Divider } from "antd";
import { PayUsingWalletIcon, WalletIcon } from "../../../assets/svg";
import { getSystemSettingsAction } from "../../../redux/services/addon-and-sponsorship/addonAndSponsorshipServices";
import { getWalletInfoAction } from "../../../redux/services/wallet-info/walletInfoServices";
import { toast } from "react-toastify";

const withStripePayment = (WrappedComponent) => {
  const HOCComponent = (props) => {
    const stripe = useStripe();
    const elements = useElements();
    const { userCards } = useSelector((state) => state.payment);
    const [selectedCard, setSelectedCard] = useState("");
    const [selectedCardObj, setSelectedCardObj] = useState(null);
    const [, setNewCardToken] = useState("");
    const [coupon_code, setCouponCode] = useState("");
    const dispatch = useDispatch();

    /* i18n dependencies */
    const { t } = useTranslation();
    /* i18n dependencies */

    /* loading dependencies */
    const [loading, setLoading] = useState(false);
    const [validateCouponLoader] = useState(false);
    const [submitLoader, setSubmitLoader] = useState(false);
    /* loading dependencies */

    const [couponCodeMessage, setCouponCodeMessage] = useState("");

    /* form dependencies */
    const [form] = Form.useForm();
    const pay_using_wallet_points = Form.useWatch("pay_using_wallet_points", {
      form,
      preserve: true,
    });
    const cardholderName = Form.useWatch("cardholderName", { form, preserve: true });
    const { walletInfo } = useSelector((state) => state.walletInfo);
    /* form dependencies */

    useEffect(() => {
      const dataToBeSent = {
        page: 1,
        limit: 10
      };
      dispatch(getWalletInfoAction(dataToBeSent, setLoading));
    }, [dispatch]);

    /* on card changed */
    const onCardChanged = (value) => {
      const tempSelectedCardObj = userCards?.data?.find((element)=>element.id === value);
      setSelectedCard(value);
      setSelectedCardObj(tempSelectedCardObj)
      form.resetFields(["card_number", "expiry_date", "cvc_number"]);
      form.setFieldValue("cardholderName", tempSelectedCardObj?.name || "")
    };
    /* on card changed */

    const onSubmit = async () => {
      let cardElement = elements.getElement(CardNumberElement);

      setSubmitLoader(true);
      // If a card is selected, create payment with the selected card
      if (selectedCard) {
        props.createPayment(null, selectedCard, resetDefaultFields, pay_using_wallet_points);
      } else {
        // If no card selected, create a new token
        const payload = await stripe.createToken(cardElement, {
          name : cardholderName
        });

        console.log(props?.totalAmount,'props?.totalAmount')
        if (
          props?.totalAmount > walletInfo?.wallet?.wallet_balance &&
          payload?.error?.type === "validation_error"
        ) {
          toastService.error(t("CommonUtils.insufficientWalletBalance"));
          return;
        } else {
          if (payload?.token?.card?.id) {
            const newCardToken = payload?.token;
            setNewCardToken(newCardToken);
            // Create payment with the new token
            props.createPayment(newCardToken, null, resetDefaultFields, pay_using_wallet_points);
          } else {
            setSubmitLoader(false);
            props.createPayment(null, null, resetDefaultFields, pay_using_wallet_points);
            // Handle the error (e.g., show an error message to the user)
          }
        }
      }
    };

    const onValidateCouponCode = async (e) => {
      e.preventDefault();
      props.validateCouponCode(coupon_code, setCouponCodeMessage);
    };

    const resetDefaultFields = () => {
      form.resetFields();
    };

    return (
      <>
        <Box>
          <div
            style={{
              borderRadius: 12,
              boxShadow: `0 1px 2px 0 rgba(0, 0, 0, 0.1), 0 2px 7px 0 rgba(0, 0, 0, 0.1)`,
              overflow: "hidden"
            }}
          >
            {loading ? (
              <Spinner />
            ) : (
              <Form
                form={form}
                layout="vertical"
                autoComplete="off"
                requiredMark={false}
                onFinish={onSubmit}
                style={{ height: "100%" }}
              >
                <Row gutter={[16, 16]} style={{ height: "100%" }}>
                  <Col span={12}>
                    <WrappedComponent
                      couponAppliedMessage={couponCodeMessage}
                      getAppliedCouponDetails={props.getAppliedCouponDetails}
                    />
                  </Col>
                  <Col span={12} style={{ padding: 30 }}>
                    <Flex vertical justify="space-between">
                      <div>
                        <Form.Item
                          label={
                            <RequiredLabel
                              text={t("CommonUtils.cardNumber")}
                              required={true}
                            />
                          }
                          name="card_number"
                          rules={[
                            {
                              required:
                                (selectedCard &&
                                  selectedCard !== null &&
                                  selectedCard !== undefined &&
                                  selectedCard !== "") ||
                                pay_using_wallet_points ||
                                Number(props.totalAmount) <= 0
                                  ? false
                                  : true,
                              message: t("CommonUtils.enterCardNumber"),
                            },
                          ]}
                        >
                          <CardNumberElement
                            onChange={(e) => {
                              form.setFieldValue("select_card", null);
                              setSelectedCard("");
                            }}
                            className="stripe-input"
                          />
                        </Form.Item>

                        <Form.Item
                          label={
                            <RequiredLabel
                              text={t("CommonUtils.expiryDate")}
                              required={true}
                            />
                          }
                          name="expiry_date"
                          rules={[
                            {
                              required:
                                (selectedCard &&
                                  selectedCard !== null &&
                                  selectedCard !== undefined &&
                                  selectedCard !== "") ||
                                pay_using_wallet_points ||
                                Number(props.totalAmount) <= 0
                                  ? false
                                  : true,
                              message: t("CommonUtils.enterExpiryDate"),
                            },
                          ]}
                        >
                          <CardExpiryElement className="stripe-input" />
                        </Form.Item>

                        <Form.Item
                          label={
                            <RequiredLabel
                              text={t("CommonUtils.cvcNumber")}
                              required={true}
                            />
                          }
                          name="cvc_number"
                          rules={[
                            {
                              required:
                                (selectedCard &&
                                  selectedCard !== null &&
                                  selectedCard !== undefined &&
                                  selectedCard !== "") ||
                                pay_using_wallet_points ||
                                Number(props.totalAmount) <= 0
                                  ? false
                                  : true,
                              message: t("CommonUtils.enterCvcNumber"),
                            },
                          ]}
                        >
                          <CardCvcElement className="stripe-input" />
                        </Form.Item>

                        <Form.Item
                        label={
                          <RequiredLabel
                            text={t("Modules.Services.ServicesForm.cardholderName")}
                            required={true}
                          />
                        }
                        name="cardholderName"
                        rules={[
                          {
                            required:
                            (selectedCard &&
                              selectedCard !== null &&
                              selectedCard !== undefined &&
                              selectedCard !== "") ||
                            pay_using_wallet_points ||
                            Number(props.totalAmount) <= 0
                              ? false
                              : true,
                            message: t(
                              "Modules.Services.ServicesForm.pleaseEnterCardholderName"
                            ),
                          },
                          {
                            whitespace: true,
                            message: t(
                              "Modules.Services.ServicesForm.pleaseEnterCardholderName"
                            ),
                          },
                        ]}
                        normalize={(value) => sanitizeDataTypeClientSide(value)}
                      >
                        <Input
                          placeholder={t(
                            "Modules.Services.ServicesForm.enterCardholderName"
                          )}
                          disabled={selectedCard && selectedCard !== "" && selectedCard !== null && selectedCard !== undefined}
                        />
                      </Form.Item>
                        {/* <CardElement /> */}
                      </div>
                      {userCards?.data?.length > 0 && <Divider>OR</Divider>}
                      <div>
                        <Row gutter={[16, 16]} align="center" justify="center">
                          <Col span={12}>
                            {userCards?.data?.length > 0 && (
                              <Form.Item
                                name="select_card"
                                label={
                                  <RequiredLabel
                                    text={t("CommonUtils.selectCard")}
                                    required={false}
                                  />
                                }
                              >
                                <Select
                                  placeholder={t("CommonUtils.selectCard")}
                                  options={userCards?.data?.map((element) => {
                                    return {
                                      value: element.id,
                                      label: `${element.brand} ****${element.last4}`,
                                    };
                                  })}
                                  onChange={onCardChanged}
                                  allowClear
                                />
                              </Form.Item>
                            )}
                          </Col>
                          <Col
                            span={userCards?.data?.length > 0 ? 12 : 24}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              marginBottom: 10,
                            }}
                          >
                            {props.showPayUsingWalletPointsCheckbox && walletInfo?.wallet?.wallet_balance > 0 && (
                              <>
                                <Card
                                  bordered
                                  hoverable
                                  style={{
                                    width: "100%",
                                    padding: 0,
                                    borderRadius: 15,
                                    border: `1px solid ${colors.oceanBlue}`
                                  }}
                                >
                                  <Row
                                    gutter={[24, 24]}
                                    align="middle"
                                    justify="center"
                                  >
                                    <Col span={4}>
                                      <PayUsingWalletIcon
                                        fillColor={colors.primaryColor}
                                      />
                                    </Col>
                                    <Col span={16}>
                                      <Flex vertical>
                                        <Typography.Title level={4}>
                                          {t("AppModuleNames.Provider.Wallet")}
                                        </Typography.Title>
                                        <Typography.Title
                                          level={2}
                                          style={{
                                            margin: 0,
                                            color: colors.electricBlue,
                                          }}
                                        >
                                          {walletInfo?.wallet?.wallet_balance?.toFixed(2) ||
                                            0}{" "}
                                          {t("CommonUtils.points")}
                                        </Typography.Title>
                                      </Flex>
                                    </Col>
                                    <Col span={4}>
                                      <Form.Item
                                        name="pay_using_wallet_points"
                                        valuePropName="checked"
                                      >
                                        <Checkbox />
                                      </Form.Item>
                                    </Col>
                                  </Row>
                                </Card>
                              </>
                            )}
                          </Col>
                        </Row>
                      </div>
                      {props.applyCouponCode && (
                        <div>
                          <Row gutter={[16, 16]}>
                            <Col span={16}>
                              <Form.Item
                                label={
                                  <RequiredLabel
                                    text={t("Modules.CouponCodes.code")}
                                    required={false}
                                  />
                                }
                                name="coupon_code"
                                normalize={(value) =>
                                  sanitizeDataTypeClientSide(value)
                                }
                              >
                                <Input
                                  style={{
                                    border:
                                      couponCodeMessage !== "" &&
                                      couponCodeMessage !== undefined &&
                                      couponCodeMessage !== null
                                        ? `1px solid ${colors.limeGreen}`
                                        : "",
                                  }}
                                  placeholder={t(
                                    "Modules.DiscountCodes.DiscountCodesForm.enterCode"
                                  )}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    if(!value) {
                                      setCouponCodeMessage("")
                                      dispatch(Actions.createAction(Actions.CLEAR_COUPON));
                                      form.setFieldValue("coupon_code", "");
                                    }
                                    setCouponCode(e.target.value)
                                  }}
                                />
                              </Form.Item>
                            </Col>
                            <Col
                              span={4}
                              style={{
                                textAlign: "center",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Button
                                type="primary"
                                loading={props.validateCouponCodeLoader}
                                style={{ width: "100%", marginTop: 10 }}
                                disabled={
                                  !stripe ||
                                  validateCouponLoader ||
                                  (couponCodeMessage !== "" &&
                                    couponCodeMessage !== null &&
                                    couponCodeMessage !== undefined)
                                }
                                onClick={(e) => onValidateCouponCode(e)}
                              >
                                {couponCodeMessage !== "" &&
                                couponCodeMessage !== null &&
                                couponCodeMessage !== undefined
                                  ? t("CommonUtils.applied")
                                  : t("CommonUtils.apply")}
                              </Button>
                            </Col>
                            <Col
                              span={4}
                              style={{
                                textAlign: "center",
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <Button
                                type="dashed"
                                loading={props.validateCouponCodeLoader}
                                style={{ width: "100%", marginTop: 10 }}
                                disabled={
                                  props.submitLoader ||
                                  !stripe ||
                                  props.validateCouponCodeLoader
                                }
                                onClick={() => {
                                  setCouponCodeMessage("");
                                  dispatch(
                                    Actions.createAction(Actions.CLEAR_COUPON)
                                  );
                                  form.setFieldValue("coupon_code", "");
                                }}
                              >
                                {t("CommonUtils.clear")}
                              </Button>
                            </Col>
                          </Row>
                          {couponCodeMessage !== "" &&
                            couponCodeMessage !== null &&
                            couponCodeMessage !== undefined && (
                              <Typography.Title
                                level={4}
                                style={{
                                  color: `${colors.limeGreen}`,
                                  margin: 0,
                                  display: "flex",
                                  gap: "0.3rem",
                                }}
                              >
                                {couponCodeMessage}
                                <CheckCircleFilled style={{ fontSize: 16 }} />
                              </Typography.Title>
                            )}
                        </div>
                      )}
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={props.submitLoader}
                        style={{ width: "100%", marginTop: 20 }}
                        disabled={props.submitLoader || !stripe}
                      >
                        {t("CommonUtils.pay")}
                      </Button>
                    </Flex>
                  </Col>
                </Row>
              </Form>
            )}
          </div>
          <PaymentConfirmationDialog
            open={props.openPaymentConfirmationDialog}
            onClose={() => {
              props.setOpenPaymentConfirmationDialog(false);
              dispatch(
                Actions.createAction(
                  Actions.SELECTED_SUBSCRIPTION_PLAN_DETAILS,
                  { selectedSubscriptionPlanDetails: {} }
                )
              );
              dispatch(
                Actions.createAction(Actions.GET_VALIDATE_COUPON, {
                  validateCouponData: {},
                })
              );
            }}
          />
        </Box>
      </>
    );
  };
  return HOCComponent;
};

export default withStripePayment;
